import React, { useEffect, useState } from "react";
import {
  Autocomplete,
  Button,
  FormLabel,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import { textStyle } from "../../styles/Style";
import "../../../components/styles/popupStyle.css";
import {
  validateString,
  validationLength,
  validationType,
  validationMessages,
} from "../../actions/validations";
import { checkData, deleteItem, updateData } from "../../actions/actionItems";
import DraggableItemList from "../../subcomponents/DNDComponent";
import { buttonValues } from "../../../constants/Types";
import { SnCPropertyEnum } from "../../../constants/NavbarList";
import { ClearButton } from "../../subcomponents/ClearButton";
import { CopyButton } from "../../subcomponents/CopyButton";
import { golfTooltipHeaders } from "../../../constants/TooltipInformation";
import { tooltipComponent } from "../../subcomponents/tooltipComponent";
import AlertComponent from "../../subcomponents/AlertComponent";

const DAY_OPTIONS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const TYPE_OPTIONS = ["Weekday", "Weekend"];

const SnCWeekConfiguration = ({
  data,
  fieldValues,
}: {
  data: (value: any) => void;
  fieldValues: any;
}) => {
  const [day, setDay] = useState<string | null>(null);
  const [type, setType] = useState<string>("Weekday");
  const [editClicked, setEditClicked] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<"success" | "error">("success");
  const [items, setItems] = useState<any>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [lastUpdatedRecord, setLastUpdatedRecord] = useState<any>(null);
  const [dayFieldError, setDayFieldError] = useState("");
  const [typeFieldError, setTypeFieldError] = useState("");

  useEffect(() => {
    fieldValues && setItems(Array.isArray(fieldValues) ? fieldValues : []);
  }, []);

  useEffect(() => {
    data(items);
    setSelectedId(checkData(items, selectedId) ? selectedId : null);
  }, [items]);

  const payload = {
    Day: day,
    Type: type,
  };

  const validateInput = (): boolean => {
    setDayFieldError("");
    setTypeFieldError("");
    const dayLabel = "Day";
    const typeLabel = "Type";
    if (!day) {
      setDayFieldError(dayLabel + validationMessages.sncMandatorySuffix);
    }
    if (!type) {
      setTypeFieldError(typeLabel + validationMessages.sncMandatorySuffix);
    }
    if (!day || !type) {
      setAlertMessage(validationMessages.sncAllMandatory);
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }
    if (
      !validateString(day, validationLength.originCode, validationType.CODE)
    ) {
      setDayFieldError(validationMessages.originCode);
      return false;
    }
    if (
      !validateString(type, validationLength.originName, validationType.OTHER)
    ) {
      setTypeFieldError(validationMessages.originName);
      return false;
    }
    return true;
  };

  const updateItem = () => {
    if (!validateInput()) {
      return;
    }
    const otherItems = (items ?? []).filter(
      (item: any) => item.id !== selectedId,
    );
    if (
      otherItems?.some(
        (item: any) => item["Day"] === day && item["Type"] === type,
      )
    ) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    setItems((prev: any) =>
      (prev ?? []).map((item: any) =>
        item.id === selectedId ? { ...item, ...payload } : item,
      ),
    );
    editItem(null);
    setEditClicked(false);
    setSelectedId(null);
    setAlertMessage(
      `${SnCPropertyEnum.weekConfiguration} updated successfully`,
    );
    setAlertType("success");
    setOpenAlert(true);
  };

  const addItem = () => {
    if (!validateInput()) {
      return;
    }
    if (
      (items ?? []).some(
        (item: any) => item["Day"] === day && item["Type"] === type,
      )
    ) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    setItems((prev: any) =>
      updateData(
        prev?.length
          ? [...prev, { id: (prev?.length ?? 0) + 1, ...payload }]
          : [{ id: 1, ...payload }],
      ),
    );
    setLastUpdatedRecord(payload);
    editItem(null);
    setEditClicked(false);
  };

  const editItem = (list: any) => {
    list?.id && setSelectedId(list.id);
    if (list != null) {
      setEditClicked(true);
      setDayFieldError("");
      setTypeFieldError("");
      setDay(list["Day"] ?? null);
      setType(list["Type"] ?? "Weekday");
    } else {
      setDay(null);
      setType("Weekday");
      setDayFieldError("");
      setTypeFieldError("");
    }
  };

  const deleteItemById = (id: number) => {
    setItems(deleteItem(items ?? [], id));
  };

  return (
    <Stack direction="column" gap="10px">
      <Paper
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "10px",
          padding: "20px",
        }}
      >
        <Stack
          direction="row"
          width="100%"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h6" sx={textStyle}>
            Week Configuration
            <Tooltip
              title={tooltipComponent(
                golfTooltipHeaders?.categoryGroup ?? "Week Configuration",
              )}
            >
              <InfoIcon fontSize="small" />
            </Tooltip>
          </Typography>
          <Stack direction="row" gap="10px">
            {editClicked && (
              <ClearButton
                click={() => {
                  editItem(null);
                  setSelectedId(null);
                }}
              />
            )}
            {lastUpdatedRecord && (
              <CopyButton click={() => editItem(lastUpdatedRecord)} />
            )}
          </Stack>
        </Stack>
        <Stack direction="column" width="100%" gap="20px">
          <Stack direction="column">
            <FormLabel component="legend">
              Day
              <Tooltip title={tooltipComponent(golfTooltipHeaders.taxName)}>
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>{" "}
            <Autocomplete
              size="small"
              options={DAY_OPTIONS}
              value={day}
              onChange={(_, v) => {
                setDay(v ?? null);
                setDayFieldError("");
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  error={!!dayFieldError}
                  helperText={dayFieldError}
                />
              )}
            />
          </Stack>
          <Stack direction="column">
            <FormLabel component="legend">
              Type*
              <Tooltip title={tooltipComponent(golfTooltipHeaders.taxName)}>
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>{" "}
            <Autocomplete
              size="small"
              options={TYPE_OPTIONS}
              value={type}
              onChange={(_, v) => {
                setType(v ?? "Weekday");
                setTypeFieldError("");
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  error={!!typeFieldError}
                  helperText={typeFieldError}
                />
              )}
            />
          </Stack>
        </Stack>
        <Stack direction="row" justifyContent="flex-end">
          <Button
            variant="contained"
            sx={{
              cursor: "pointer",
              border: "1px solid gray",
              borderRadius: "10px",
              background: "darkgreen",
              color: "white",
            }}
            onClick={() => (selectedId ? updateItem() : addItem())}
          >
            {selectedId ? buttonValues.UPDATE : buttonValues.ADD} Week
            Configuration
          </Button>
        </Stack>
        <Stack direction="column" gap="10px" mt={4}>
          <Typography variant="body2" sx={{ color: "red", fontWeight: 800 }}>
            Do not use sample data. Those are for demo purposes only!
          </Typography>
          <Typography variant="h6">Week Configuration Available</Typography>
          {items?.length ? (
            <DraggableItemList
              itemData={items}
              onOrderUpdate={(order) => setItems(order)}
              onEdit={editItem}
              onDelete={deleteItemById}
              code="Day"
              name="Type"
            />
          ) : (
            <Typography variant="body2">
              No Week Configuration Available
            </Typography>
          )}
        </Stack>
      </Paper>
      <AlertComponent
        message={alertMessage}
        type={alertType}
        alertOpen={openAlert}
        closeAlert={() => setOpenAlert(false)}
      />
    </Stack>
  );
};

export default SnCWeekConfiguration;
